/**
 * Stored analytics results (API snapshots/): the board, huddle, scorecard,
 * schedule analysis and Goals answer from numbers the worker keeps current,
 * with `snapshot: { computedAt, ageSeconds, stale }`. A first view of an
 * office set or query the worker has not stored yet answers 202
 * `{ preparing, retryAfterMs }`: the request throws SnapshotPreparingError
 * and the query client asks again after `retryAfterMs` (App.tsx), while the
 * card says "Preparing" instead of showing a skeleton.
 */
import type { SnapshotInfo } from '@velora/contracts/analytics';
import { SnapshotPreparingSchema } from '@velora/contracts/analytics';
import type { HttpClient } from '@velora/sdk';
import { z } from 'zod';

export class SnapshotPreparingError extends Error {
  constructor(readonly retryAfterMs: number) {
    super('These numbers are being prepared.');
    this.name = 'SnapshotPreparingError';
  }
}

export function isSnapshotPreparing(err: unknown): err is SnapshotPreparingError {
  return err instanceof SnapshotPreparingError;
}

/** A first result takes a cold worker up to a few minutes: keep asking that long. */
const PREPARING_ATTEMPTS = 90;

/** Query client retry: keep asking while preparing, else `fallback` retries. */
export function snapshotRetry(fallback: number) {
  return (failureCount: number, err: unknown): boolean =>
    isSnapshotPreparing(err) ? failureCount < PREPARING_ATTEMPTS : failureCount < fallback;
}

export function snapshotRetryDelay(failureCount: number, err: unknown): number {
  if (isSnapshotPreparing(err)) return Math.max(500, err.retryAfterMs);
  return Math.min(1000 * 2 ** failureCount, 30_000);
}

/** GET a snapshot route: the result, or SnapshotPreparingError on 202. */
export async function snapshotRequest<T>(
  c: HttpClient,
  path: string,
  schema: z.ZodType<T>,
  signal?: AbortSignal,
): Promise<T> {
  const r = await c.request(path, {
    schema: z.union([SnapshotPreparingSchema, schema]),
    ...(signal ? { signal } : {}),
  });
  const p = SnapshotPreparingSchema.safeParse(r);
  if (p.success) throw new SnapshotPreparingError(p.data.retryAfterMs);
  return r as T;
}

/** Ask again soon while the server is refreshing a stale result. */
export const SNAPSHOT_STALE_REFETCH_MS = 5000;

export function refetchWhileStale(
  normalMs: number | false,
): (q: {
  state: { data?: { snapshot?: SnapshotInfo | undefined } | undefined };
}) => number | false {
  return (q) => (q.state.data?.snapshot?.stale ? SNAPSHOT_STALE_REFETCH_MS : normalMs);
}

/** "Updated just now" / "Updated 4 min ago" / "Updated 2 h ago" (null for a live answer). */
export function asOfLabel(info: SnapshotInfo | undefined, nowMs = Date.now()): string | null {
  if (!info || info.source !== 'snapshot') return null;
  const at = Date.parse(info.computedAt);
  if (Number.isNaN(at)) return null;
  const min = Math.max(0, Math.floor((nowMs - at) / 60_000));
  if (min < 1) return 'Updated just now';
  if (min < 60) return `Updated ${min} min ago`;
  return `Updated ${Math.floor(min / 60)} h ago`;
}
